import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from, of, map, catchError, forkJoin } from 'rxjs';
import { PartnerService } from './partner.service';
import { SessionResetService } from './session-reset.service';

export interface CompanySearchResult {
  id: string;
  partnerCompanyId?: string;
  collection: 'companies' | 'companyDirectory' | 'companyRelationships';
  legalName: string;
  dbaName?: string;
  dotNumber?: string;
  mcNumber?: string;
  source: 'haulverse' | 'dot' | 'manual';
  city?: string;
  state?: string;
  isCarrier?: boolean;
  isBroker?: boolean;
}

/** The value stored when a company is selected from the search. */
export interface CompanyReference {
  id: string;
  partnerCompanyId?: string;
  collection: 'companies' | 'companyDirectory' | 'companyRelationships';
  legalName: string;
  dbaName?: string | null;
  dotNumber?: string;
  mcNumber?: string;
  source: 'haulverse' | 'dot' | 'manual';
}

@Injectable({
  providedIn: 'root',
})
export class CompanySearchService {
  private readonly functions = inject(Functions);
  private readonly partnerService = inject(PartnerService);
  private readonly sessionReset = inject(SessionResetService);
  private readonly cache = new Map<string, { results: CompanySearchResult[]; timestamp: number }>();
  private readonly CACHE_TTL = 60_000; // 1 minute

  constructor() {
    // Clear all cached state on logout
    this.sessionReset.reset$.subscribe(() => this.clearState());
  }

  /**
   * Search companies across both Haulverse accounts and the DOT directory.
   * Results are cached for 1 minute to avoid redundant Cloud Function calls.
   */
  searchCompanies(query: string): Observable<CompanySearchResult[]> {
    const trimmed = query.trim().toLowerCase();

    if (trimmed.length < 2) {
      return of([]);
    }

    // Check cache
    const cached = this.cache.get(trimmed);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return of(cached.results);
    }

    const backendSearchFn = httpsCallable<{ query: string }, { results: CompanySearchResult[] }>(
      this.functions,
      'searchCompanies'
    );

    const backendSearch$ = from(backendSearchFn({ query: trimmed })).pipe(
      map((response) => response.data.results || []),
      catchError((err) => {
        console.error('Company search backend failed:', err);
        return of([]);
      })
    );

    const localSearch$ = this.partnerService.getPartners().pipe(
      map(partners => partners.filter(p => {
        const dot = p.partnerDotNumber || p.dotNumber || '';
        const mc = p.partnerMcNumber || p.mcNumber || '';
        return (
          (p.partnerName && p.partnerName.toLowerCase().includes(trimmed)) ||
          (p.dbaName && p.dbaName.toLowerCase().includes(trimmed)) ||
          (dot && dot.includes(trimmed)) ||
          (mc && mc.includes(trimmed))
        );
      })),
      map(partners => partners.map(p => ({
        id: p.partnerCompanyId || p.id!,
        partnerCompanyId: p.partnerCompanyId || p.id!,
        collection: 'companyRelationships',
        legalName: p.partnerName,
        dbaName: p.dbaName ?? undefined,
        dotNumber: p.partnerDotNumber || p.dotNumber,
        mcNumber: p.partnerMcNumber || p.mcNumber,
        source: p.source || 'manual',
        isCarrier: p.isCarrier ?? (p.partnerTypes?.includes('carrier') || p.partnerType === 'carrier'),
        isBroker: p.isBroker ?? (p.partnerTypes?.includes('broker') || p.partnerType === 'broker'),
        city: p.address?.split(',')[0]?.trim(),
        state: p.address?.split(',')[1]?.trim(),
      } as CompanySearchResult))),
      catchError((err) => {
        console.error('Company search local failed:', err);
        return of([]);
      })
    );

    return forkJoin({
      backend: backendSearch$,
      local: localSearch$
    }).pipe(
      map(({ backend, local }) => {
        // Dedup by dotNumber, partnerCompanyId, and normalized legal/DBA names
        const localDotSet = new Set(local.filter(l => l.dotNumber).map(l => String(l.dotNumber).trim()));
        const localIdSet = new Set(local.map(l => l.partnerCompanyId || l.id).filter(Boolean));
        const localNameSet = new Set<string>();
        for (const l of local) {
          if (l.legalName) localNameSet.add(PartnerService.normalizeCompanyName(l.legalName));
          if (l.dbaName) localNameSet.add(PartnerService.normalizeCompanyName(l.dbaName));
        }

        const filteredBackend = backend.filter(b => {
          if (b.id && localIdSet.has(b.id)) return false;
          if (b.dotNumber && localDotSet.has(String(b.dotNumber).trim())) return false;
          if (b.legalName && localNameSet.has(PartnerService.normalizeCompanyName(b.legalName))) return false;
          if (b.dbaName && localNameSet.has(PartnerService.normalizeCompanyName(b.dbaName))) return false;
          return true;
        });

        // Sort local and backend results alphabetically so dropdown items are predictable
        const byName = (a: CompanySearchResult, b: CompanySearchResult) =>
          (a.legalName || '').localeCompare(b.legalName || '', undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        const sortedLocal = [...local].sort(byName);
        const sortedBackend = [...filteredBackend].sort(byName);

        // Put local saved partners first, followed by directory/DOT results (each sorted A-Z)
        const results = [...sortedLocal, ...sortedBackend];
        
        // Cache the result
        this.cache.set(trimmed, { results, timestamp: Date.now() });
        // Evict old cache entries (keep last 10)
        if (this.cache.size > 10) {
          const oldestKey = this.cache.keys().next().value;
          if (oldestKey) this.cache.delete(oldestKey);
        }
        return results;
      })
    );
  }

  /**
   * Add a company manually via Cloud Function.
   */
  addManualCompany(data: {
    legalName: string;
    dbaName?: string;
    dotNumber?: string;
    mcNumber?: string;
    isCarrier?: boolean;
    isBroker?: boolean;
    city?: string;
    state?: string;
  }): Observable<CompanySearchResult> {
    const partnerType = data.isBroker ? 'broker' : data.isCarrier ? 'carrier' : 'customer';
    
    return from(
      this.partnerService.ensurePartnerExists({
        partnerType: partnerType as any,
        partnerName: data.legalName,
        dbaName: data.dbaName,
        dotNumber: data.dotNumber,
        mcNumber: data.mcNumber,
        source: 'manual',
        isCarrier: data.isCarrier,
        isBroker: data.isBroker,
        address: [data.city, data.state].filter(Boolean).join(', ') || undefined,
      })
    ).pipe(
      map(docId => {
        const canonicalId = docId.includes('_') ? docId.slice(docId.indexOf('_') + 1) : docId;
        return {
          id: canonicalId,
          partnerCompanyId: canonicalId,
          collection: 'companyRelationships',
          legalName: data.legalName,
          dbaName: data.dbaName,
          dotNumber: data.dotNumber,
          mcNumber: data.mcNumber,
          city: data.city,
          state: data.state,
          isCarrier: data.isCarrier,
          isBroker: data.isBroker,
          source: 'manual'
        } as CompanySearchResult;
      })
    );
  }

  /**
   * Convert a CompanySearchResult to a CompanyReference (for form storage).
   */
  toReference(result: CompanySearchResult): CompanyReference {
    return {
      id: result.partnerCompanyId || result.id,
      partnerCompanyId: result.partnerCompanyId || result.id,
      collection: result.collection,
      legalName: result.legalName,
      dbaName: result.dbaName || null,
      dotNumber: result.dotNumber,
      mcNumber: result.mcNumber,
      source: result.source,
    };
  }

  /**
   * Clear all cached state. Called on logout to prevent
   * stale data from leaking across user sessions.
   */
  clearState(): void {
    this.cache.clear();
  }
}
